import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'Just now';
  const now = new Date();
  const date = new Date(timestamp);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} mins ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  return date.toLocaleDateString();
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleCounts, setRoleCounts] = useState({ admin: 2, teacher: 0, student: 0, parent: 0 });
  const [securityLogs, setSecurityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  // Bulk Upload State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadRole, setUploadRole] = useState("Student");
  const [uploading, setUploading] = useState(false);

  // Add User State
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'Student', batchIdStr: '', phoneNumber: '' });
  const [creationError, setCreationError] = useState('');
  
  // Edit User State
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUserForm, setEditingUserForm] = useState({ idStr: '', name: '', email: '', password: '', role: 'Student', batchIdStr: '' });
  const [updatingUser, setUpdatingUser] = useState(false);

  const [batches, setBatches] = useState([]);

  // Master Attendance & Leave Quota (Moved from Teacher Panel)
  const [showUploadAttendanceModal, setShowUploadAttendanceModal] = useState(false);
  const [showMasterLeaveModal, setShowMasterLeaveModal] = useState(false);
  const [attendanceType, setAttendanceType] = useState('Teacher'); // 'Teacher' or 'Student'
  const [attendanceFile, setAttendanceFile] = useState(null);
  const [leaveQuotas, setLeaveQuotas] = useState({ cl: 12, sl: 10, pl: 5 });
  const [selectedTeacherEmailForLeave, setSelectedTeacherEmailForLeave] = useState('');

  // Biometric API State
  const [showBiometricModal, setShowBiometricModal] = useState(false);
  const [biometricForm, setBiometricForm] = useState({
    biometricApiUrl: '',
    biometricApiMethod: 'GET',
    biometricCorporateId: '',
    biometricUsername: '',
    biometricPassword: '',
    biometricApiEnabled: false,
    biometricSyncStartTime: "08:00",
    biometricSyncEndTime: "20:00"
  });
  const [biometricActiveTab, setBiometricActiveTab] = useState('URL');
  const [syncing, setSyncing] = useState(false);
  const [biometricHeaders, setBiometricHeaders] = useState([{ key: '', value: '' }]);
  const [biometricParams, setBiometricParams] = useState([{ key: '', value: '' }]);
  const [biometricMapping, setBiometricMapping] = useState({
    empCodeField: 'EmpCode',
    dateField: 'PunchDate',
    timeField: 'PunchTime'
  });

  const fetchUsers = useCallback(async () => {
      setLoading(true);
      try {
        const sessionUser = JSON.parse(localStorage.getItem('user') || '{}');
        const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
        
        const results = await Promise.allSettled([
          axios.get(API_BASE_URL + '/api/students', { headers }),
          axios.get(API_BASE_URL + '/api/teachers', { headers })
        ]);

        let studentData = [];
        let teacherData = [];

        if (results[0].status === 'fulfilled') {
          const res = results[0].value;
          studentData = (Array.isArray(res.data) ? res.data : []).map(st => ({
            id: `PEN-${st.id.toString().padStart(4, '0')}`,
            name: st.name,
            email: st.email || '',
            batchIdStr: st.batchIdStr || '',
            role: st.role || "Student",
            status: st.status || "Active",
            lastLogin: "Today",
            phoneNumber: st.personalPhoneNumber || '',
            studentClass: st.studentClass || '',
            stream: st.stream || '',
            board: st.board || ''
          }));
        } else {
          console.error("Student fetch failed:", results[0].reason);
          toast.error("Could not fetch student list. Roster may be incomplete.");
        }

        if (results[1].status === 'fulfilled') {
          const res = results[1].value;
          teacherData = (Array.isArray(res.data) ? res.data : []).map(t => ({
            id: `PEN-T${t.id.toString().padStart(3, '0')}`,
            name: t.name,
            email: t.teacherEmail || '',
            batchIdStr: '',
            role: t.role ? (t.role.charAt(0).toUpperCase() + t.role.slice(1)) : "Teacher",
            status: "Active",
            lastLogin: "Today",
            department: t.department || '',
            initials: t.initials || '',
            title: t.title || '',
            phoneNumber: t.phoneNumber || ''
          }));
        } else {
          console.error("Teacher fetch failed:", results[1].reason);
          toast.error("Could not fetch teacher list.");
        }

        setUsers([...teacherData, ...studentData]);
        setRoleCounts(prev => ({
          ...prev,
          teacher: teacherData.length,
          student: studentData.length
        }));
      } catch (err) {
        console.error("Global fetch error:", err);
        toast.error("Failed to connect to the User Identity service.");
      } finally {
        setLoading(false);
      }
  }, []);

  const fetchSecurityLogs = useCallback(async () => {
    try {
      const sessionUser = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
      const res = await axios.get(`${API_BASE_URL}/api/security-logs`, { headers });
      setSecurityLogs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch security logs:", err);
    } finally {
      setLoadingLogs(false);
    }
  }, []);





  useEffect(() => {
    fetchUsers();
    fetchSecurityLogs();
    
    const sessionUser = JSON.parse(localStorage.getItem('user') || "{}");
    const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
    axios.get(API_BASE_URL + '/api/batches', { headers })
      .then(res => setBatches(Array.isArray(res.data) ? res.data : [])).catch(console.error);

    // Fetch Biometric Settings (with cache busting)
    axios.get(`${API_BASE_URL}/api/theme?t=${Date.now()}`, { headers })
      .then(res => {
        if (res.data) {
          const d = res.data;
          setBiometricForm({
            biometricApiUrl: d.biometricApiUrl || d.biometric_api_url || '',
            biometricApiMethod: d.biometricApiMethod || d.biometric_api_method || 'GET',
            biometricCorporateId: d.biometricCorporateId || d.biometric_corporate_id || '',
            biometricUsername: d.biometricUsername || d.biometric_username || '',
            biometricPassword: d.biometricPassword || d.biometric_password || '',
            biometricApiEnabled: d.biometricApiEnabled || d.biometric_api_enabled || false
          });
          
          const hStr = d.biometricApiHeaders || d.biometric_api_headers;
          if (hStr) {
            try {
              const h = JSON.parse(hStr);
              const hArray = Object.entries(h).map(([k, v]) => ({ key: k, value: v }));
              setBiometricHeaders(hArray.length > 0 ? hArray : [{ key: '', value: '' }]);
            } catch (e) { console.error("Header parse error", e); }
          }

          const pStr = d.biometricApiParams || d.biometric_api_params;
          if (pStr) {
            try {
              const p = JSON.parse(pStr);
              const pArray = Object.entries(p).map(([k, v]) => ({ key: k, value: v }));
              setBiometricParams(pArray.length > 0 ? pArray : [{ key: '', value: '' }]);
            } catch (e) { console.error("Param parse error", e); }
          }

          const mStr = d.biometricApiMapping || d.biometric_api_mapping;
          if (mStr) {
            try {
              setBiometricMapping(JSON.parse(mStr));
              setBiometricForm(prev => ({
                ...prev,
                biometricSyncStartTime: res.data.biometricSyncStartTime || "08:00",
                biometricSyncEndTime: res.data.biometricSyncEndTime || "20:00",
                biometricApiEnabled: res.data.biometricApiEnabled
              }));
            } catch (e) { console.error("Mapping parse error", e); }
          }
        }
      }).catch(console.error);
  }, [fetchUsers, fetchSecurityLogs]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email) {
      toast("fill all core fields");
      return;
    }
    setAddingUser(true);
    setCreationError('');
    try {
      const sessionUser = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
      
      await axios.post(API_BASE_URL + '/api/register', {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        batchIdStr: newUser.batchIdStr,
        phoneNumber: newUser.phoneNumber
      }, { headers });

      setShowAddUserModal(false);
      setCreationError('');
      setNewUser({ name: '', email: '', password: '', role: 'Student', batchIdStr: '', phoneNumber: '' });
      fetchUsers();
      toast.success("User created and assigned to your institute!");
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.message || "Failed to create user. Ensure email is unique!";
      setCreationError(message);
      toast(message);
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to permanently delete the user: ${user.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      const isTeacher = user.id.startsWith('PEN-T');
      const numericId = user.id.replace('PEN-T', '').replace('PEN-', '');
      const deleteUrl = isTeacher ? `${API_BASE_URL}/api/teachers/${numericId}` : `${API_BASE_URL}/api/students/${numericId}`;
      
      const sessionUser = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };

      await axios.delete(deleteUrl, { headers });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete user. Check server logs.");
    }
  };

  const handleEditUserClick = (user) => {
    setEditingUserForm({
      idStr: user.id,
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role && user.role.includes("Teacher") ? "Teacher" : "Student",
      batchIdStr: user.batchIdStr || '',
      // Teacher fields
      department: user.department || '',
      initials: user.initials || '',
      title: user.title || '',
      // Student fields
      phoneNumber: user.phoneNumber || '',
      studentClass: user.studentClass || '',
      stream: user.stream || '',
      board: user.board || ''
    });
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUserForm.name || !editingUserForm.email) {
      toast("Name and Email are mandatory fields!");
      return;
    }
    setUpdatingUser(true);
    try {
      const sessionUser = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
      
      await axios.put(API_BASE_URL + '/api/admin/update-user', editingUserForm, { headers });
      setShowEditUserModal(false);
      fetchUsers();
      toast.success("Profile updated successfully");
    } catch (err) {
      console.error(err);
      toast("Failed to modify User parameters. View console for output.");
    } finally {
      setUpdatingUser(false);
    }
  };

  const USER_ROLES = [
    { role: "Teacher & HOD", count: roleCounts.teacher, color: "from-blue-500 to-indigo-600", bg: "bg-blue-500/10", text: "text-blue-400" },
    { role: "Student", count: roleCounts.student, color: "from-emerald-400 to-teal-500", bg: "bg-emerald-500/10", text: "text-emerald-400" },
  ];

  const downloadSampleFormat = () => {
    let csvContent = "";
    let filename = "";
    if (uploadRole === "Student") {
        csvContent = "ID,Name,Email,Role,Status,Batch,Department,Title,Phone Number\n" +
                     ",Rohan Desai,rohan@example.com,Student,Active,B-2024,,,9876543210\n" +
                     ",Priya Sharma,priya@example.com,Student,Active,B-2024,,,9876543211\n";
        filename = "Sample_Students.csv";
    } else {
        csvContent = "ID,Name,Email,Role,Status,Batch,Department,Title,Phone Number\n" +
                     ",Amit Singh,amit@example.com,Teacher,Active,,Physics,Dr.,9812345678\n" +
                     ",Neha Verma,neha@example.com,Teacher,Active,,Chemistry,Ms.,9812345679\n";
        filename = "Sample_Teachers.csv";
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
        toast("Please select a file first.");
        return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);

    try {
        const sessionUser = JSON.parse(localStorage.getItem('user') || '{}');
        const headers = { 
            'Content-Type': 'multipart/form-data',
            'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' 
        };

        const endpoint = uploadRole === "Student" 
            ? API_BASE_URL + '/api/students/bulk-upload'
            : API_BASE_URL + '/api/teachers/bulk-upload';
            
        await axios.post(endpoint, formData, { headers });
        toast(`Successfully uploaded ${uploadRole} records!`);
        setShowUploadModal(false);
        setUploadFile(null);
        window.location.reload(); 
    } catch (err) {
        console.error(err);
        toast("Failed to upload file. Please check backend connection.");
    } finally {
        setUploading(false);
    }
  };

  const downloadSampleAttendance = () => {
    const csvContent = "Date,Teacher_Email,Name,Status,Time_In,Time_Out,Remarks\n" +
                       "2024-03-20,dr.verma@example.com,Dr. S. K. Verma,Present,08:30,16:45,\n" +
                       "2024-03-20,prof.singh@example.com,Prof. Amit Singh,Present,08:20,17:00,Late due to traffic\n" +
                       "2024-03-21,ms.gupta@example.com,Ms. R. Gupta,Absent,,,Medical Leave\n";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Sample_Teacher_Attendance.csv';
    link.click();
  };

  const handleUploadAttendance = async (e) => {
    e.preventDefault();
    if (!attendanceFile) {
        toast("Please select an Excel or CSV file first.");
        return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", attendanceFile);

    try {
        const sessionUser = JSON.parse(localStorage.getItem('user') || '{}');
        const headers = { 
            'Content-Type': 'multipart/form-data',
            'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' 
        };
        
        const endpoint = attendanceType === 'Teacher' 
            ? API_BASE_URL + '/api/teachers/attendance/upload'
            : API_BASE_URL + '/api/students/attendance/upload';
            
        await axios.post(endpoint, formData, { headers });
        toast(`Successfully uploaded ${attendanceType} attendance records!`);
        setShowUploadAttendanceModal(false);
        setAttendanceFile(null);
        fetchUsers(); 
    } catch (err) {
        console.error(err);
        toast(`Failed to upload ${attendanceType} attendance file.`);
    } finally {
        setUploading(false);
    }
  };

  const addHeader = () => setBiometricHeaders([...biometricHeaders, { key: '', value: '' }]);
  const removeHeader = (index) => setBiometricHeaders(biometricHeaders.filter((_, i) => i !== index));
  const updateHeader = (index, field, value) => {
    const newHeaders = [...biometricHeaders];
    newHeaders[index][field] = value;
    setBiometricHeaders(newHeaders);
  };

  const addParam = () => setBiometricParams([...biometricParams, { key: '', value: '' }]);
  const removeParam = (index) => setBiometricParams(biometricParams.filter((_, i) => i !== index));
  const updateParam = (index, field, value) => {
    const newParams = [...biometricParams];
    newParams[index][field] = value;
    setBiometricParams(newParams);
  };

  const handleSaveBiometricSettings = async (e) => {
    e.preventDefault();
    setSyncing(true);
    try {
      const sessionUser = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
      const payload = {
        ...biometricForm,
        biometricApiHeaders: JSON.stringify(biometricHeaders.reduce((acc, curr) => {
          if (curr.key) acc[curr.key] = curr.value;
          return acc;
        }, {})),
        biometricApiParams: JSON.stringify(biometricParams.reduce((acc, curr) => {
          if (curr.key) acc[curr.key] = curr.value;
          return acc;
        }, {})),
        biometricApiMapping: JSON.stringify(biometricMapping)
      };

      await axios.put(API_BASE_URL + '/api/theme', payload, { headers });
      toast.success("Biometric settings saved successfully!");
      setShowBiometricModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save biometric settings.");
    } finally {
      setSyncing(false);
    }
  };

  const handleTriggerManualSync = async () => {
    setSyncing(true);
    try {
      const sessionUser = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
      await axios.post(API_BASE_URL + '/api/biometric/sync', {}, { headers });
      toast.success("Biometric sync triggered! Attendance records will update shortly.");
      fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error("Manual sync failed. Check API configuration.");
    } finally {
      setSyncing(false);
    }
  };

  const handleExportAllUsers = () => {
    if (!users || users.length === 0) {
      toast.error("No users available to export.");
      return;
    }

    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "ID,Name,Email,Role,Status,Batch,Department,Title,Phone Number\n";

    users.forEach(u => {
      const row = [
        u.id,
        u.name,
        u.email,
        u.role,
        u.status,
        u.batchIdStr || "",
        u.department || "",
        u.title || "",
        u.phoneNumber || ""
      ].map(val => `"${val.toString().replace(/"/g, '""')}"`).join(",");
      csvContent += row + "\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `User_Roster_Export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("User roster exported successfully!");
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-200 to-slate-500">
              Identity & Access Management
            </span>
          </h1>
          <p className="mt-2 text-lg text-slate-400 max-w-2xl">
            Centralized control for user profiles, Role-Based Access Control (RBAC), and security audits.
          </p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setShowUploadAttendanceModal(true)} className="px-5 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white font-medium shadow-lg shadow-indigo-500/20 transition-all flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
            Upload Attendance
          </button>
          <button onClick={() => setShowBiometricModal(true)} className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
            Biometric Setup
          </button>
          <button onClick={() => setShowMasterLeaveModal(true)} className="px-5 py-2.5 rounded-xl bg-purple-500 hover:bg-purple-600 text-white font-medium shadow-lg shadow-purple-500/20 transition-all flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/></svg>
            Manage Leave Quota
          </button>
          <button onClick={() => { setCreationError(''); setShowAddUserModal(true); }} className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-medium transition-all shadow-lg flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" y1="8" x2="19" y2="14"></line><line x1="22" y1="11" x2="16" y2="11"></line></svg>
            Add User
          </button>
          <button onClick={() => setShowUploadModal(true)} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium transition-all shadow-lg shadow-blue-500/20 flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
            Bulk Register Users
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
        {USER_ROLES.map((role, idx) => (
          <div key={idx} className="relative rounded-2xl border border-slate-800 bg-slate-900/40 p-5 overflow-hidden group">
            <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-[40px] opacity-20 bg-gradient-to-br ${role.color}`}></div>
            <div className={`w-10 h-10 rounded-lg ${role.bg} ${role.text} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{role.role}</p>
            <h3 className="text-2xl font-bold text-white mb-2">{role.count.toLocaleString()}</h3>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        <div className="lg:col-span-2 rounded-3xl border border-slate-800 bg-slate-900/40 p-6 flex flex-col">
          <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
            <h2 className="text-lg font-semibold text-slate-200">User Roster & Permissions</h2>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button 
                onClick={handleExportAllUsers}
                className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-medium transition-all flex items-center gap-2 shadow-sm"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Export All Users
              </button>
              <div className="relative flex-1 sm:flex-none">
                <input type="text" placeholder="Search ID, Name, Role..." className="w-full sm:w-64 bg-slate-950 border border-slate-800 rounded-lg px-4 py-2 pl-9 text-xs text-white focus:outline-none focus:border-blue-500 transition-colors" />
                <svg className="absolute left-3 top-2.5 text-slate-500" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-800 text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="pb-3 font-semibold">User Identity</th>
                  <th className="pb-3 font-semibold">Role Bound</th>
                  <th className="pb-3 font-semibold">Account Status</th>
                  <th className="pb-3 font-semibold">Last Access</th>
                  <th className="pb-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-slate-500 font-medium">Loading Identity Matrix...</td>
                  </tr>
                ) : (Array.isArray(users) ? users : []).map((user, idx) => (
                  <tr key={idx} className="hover:bg-slate-800/20 transition-colors">
                    <td className="py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-200">{user.name}</span>
                      </div>
                    </td>
                    <td className="py-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                        user.role.toLowerCase() === 'institute' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                        user.role.toLowerCase().includes('teacher') ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 
                        user.role.includes('Pending') ? 'bg-slate-800 text-slate-400 border-slate-700' :
                        'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                      }`}>
                        {user.role.toLowerCase() === 'institute' ? 'Admin' : user.role}
                      </span>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${user.status === 'Active' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]'}`}></span>
                        <span className="text-xs font-medium text-slate-400">{user.status}</span>
                      </div>
                    </td>
                    <td className="py-4 text-xs text-slate-400">{user.lastLogin}</td>
                    <td className="py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => handleEditUserClick(user)} className="text-slate-400 hover:text-white transition-colors border border-slate-700 hover:border-slate-500 rounded p-1.5 bg-slate-800" title="Edit User Settings">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                        </button>
                        <button onClick={() => handleDeleteUser(user)} className="text-rose-400 hover:text-rose-300 transition-colors border border-rose-500/20 hover:border-rose-500/40 rounded p-1.5 bg-rose-500/10" title="Delete User">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-3xl border border-rose-500/20 bg-gradient-to-b from-rose-950/20 to-slate-900/40 p-6 relative overflow-hidden flex flex-col">
          <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/5 blur-[60px] rounded-full point-events-none"></div>
          
          <div className="flex items-center justify-between mb-6 relative z-10">
            <h2 className="text-lg font-semibold text-rose-200 flex items-center gap-2">
              <span className="text-rose-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
              </span>
              Security Audit Trail
            </h2>
          </div>
          
          <div className="space-y-4 relative z-10 flex-1 overflow-y-auto max-h-[600px] custom-scrollbar pr-2">
            {loadingLogs ? (
              <div className="py-12 text-center text-slate-500 text-xs animate-pulse">Loading Audit Matrix...</div>
            ) : (Array.isArray(securityLogs) ? securityLogs : []).length === 0 ? (
               <div className="py-12 text-center text-slate-500 text-xs italic">No security events logged.</div>
            ) : securityLogs.map((log, idx) => (
              <div key={idx} className="p-4 rounded-xl bg-slate-900 border border-rose-500/10 hover:border-rose-500/30 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                    log.type === 'Critical' ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' : 
                    log.type === 'Warning' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 
                    'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                  }`}>
                    {log.type} Event
                  </span>
                  <span className="text-[10px] text-slate-500">{formatTimeAgo(log.timestamp)}</span>
                </div>
                
                <h3 className="font-semibold text-slate-200 text-sm mb-1">{log.event}</h3>
                <p className="text-xs text-rose-200/70 font-medium mb-3">{log.detail}</p>
                
                <div className="flex items-center justify-between text-[10px] text-slate-500 pt-2 border-t border-slate-800">
                  <span>User: <span className="text-slate-400">{log.username || log.user}</span></span>
                  <span>IP: <span className="font-mono text-slate-400">{log.ip}</span></span>
                </div>
              </div>
            ))}
          </div>
          
          <button className="w-full mt-6 py-2.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 font-medium text-sm rounded-xl border border-rose-500/20 transition-colors relative z-10">
            Acknowledge Warnings
          </button>
        </div>

      </div>

      {showUploadModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-blue-400"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg></span>
                  Bulk Registration
                </h2>
                <p className="text-sm text-slate-400 mt-1">Upload an Excel/CSV file to instantly register multiple users.</p>
              </div>
              <button onClick={() => { setShowUploadModal(false); setUploadFile(null); }} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <form className="space-y-6" onSubmit={handleBulkUpload}>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">User Role Type</label>
                <select 
                  value={uploadRole}
                  onChange={(e) => setUploadRole(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all cursor-pointer"
                >
                  <option value="Student">Bulk Register Students</option>
                  <option value="Teacher">Bulk Register Teachers</option>
                </select>
              </div>
              <div className="flex flex-col gap-2 bg-blue-500/10 border border-blue-500/20 p-4 rounded-xl">
                 <div className="text-sm text-blue-300 font-medium tracking-wide">Standardized Format Required</div>
                 <p className="text-xs text-blue-400/80 mb-2">Ensure emails are unique. All new users will be initialized with the default password: <span className="text-blue-200 font-bold">admin@123</span></p>
                 <button type="button" onClick={downloadSampleFormat} className="text-xs font-bold uppercase tracking-wider bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 mt-1 rounded-lg shadow-lg self-start transition-colors">Download {uploadRole} Sample Template</button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">User Registry Data (.csv)</label>
                <div className="relative group cursor-pointer h-32 w-full rounded-2xl border-2 border-dashed border-slate-700 hover:border-blue-500 bg-slate-900/50 hover:bg-blue-500/5 transition-colors flex flex-col items-center justify-center text-center p-4">
                  <input 
                    type="file" 
                    accept=".csv" 
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                  />
                  {!uploadFile ? (
                    <>
                      <svg className="w-8 h-8 text-slate-500 group-hover:text-blue-400 mb-2 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                      <p className="text-sm text-slate-400 group-hover:text-blue-300 transition-colors">Drag & drop your file here or click</p>
                    </>
                  ) : (
                    <>
                      <svg className="w-8 h-8 text-emerald-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      <p className="text-sm text-emerald-300 font-medium truncate max-w-full px-4">{uploadFile.name}</p>
                    </>
                  )}
                </div>
              </div>
              <button 
                type="submit" 
                disabled={uploading || !uploadFile}
                className="w-full py-3.5 rounded-xl bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-lg transition-all shadow-lg shadow-blue-500/20 flex justify-center items-center gap-2"
              >
                {uploading ? (
                  <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Parsing File...</>
                ) : 'Execute Batch Upload'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showAddUserModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl animate-in zoom-in-95 duration-200 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-slate-800/20 blur-[60px] rounded-full pointer-events-none"></div>
            
            <div className="flex justify-between items-center mb-6 relative z-10">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-slate-400">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" y1="8" x2="19" y2="14"></line><line x1="22" y1="11" x2="16" y2="11"></line></svg>
                  </span>
                  Manual Registration
                </h2>
                <p className="text-sm text-slate-400 mt-1">Provision a single identity credential into the cloud.</p>
              </div>
              <button onClick={() => { setShowAddUserModal(false); setCreationError(''); }} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            {creationError && (
              <div className="mb-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                {creationError}
              </div>
            )}
            <form className="space-y-5 relative z-10" onSubmit={handleAddUser}>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">Full Name</label>
                <input 
                  type="text" 
                  autoFocus
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 outline-none transition-all placeholder:text-slate-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">Email Address</label>
                <input 
                  type="email" 
                  required
                  placeholder="name@domain.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 outline-none transition-all placeholder:text-slate-700"
                />
              </div>
              {(newUser.role === 'Student' || newUser.role === 'Teacher') && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">Phone Number (Optional)</label>
                  <input 
                    type="text" 
                    placeholder="e.g. 9876543210"
                    value={newUser.phoneNumber}
                    onChange={(e) => setNewUser({...newUser, phoneNumber: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 outline-none transition-all placeholder:text-slate-700"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">Assign Role</label>
                  <select 
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-slate-600 focus:ring-1 focus:ring-slate-600 outline-none transition-all"
                  >
                    <option value="Student">Student</option>
                    <option value="Teacher">Teacher</option>
                    <option value="Institute">Administrator</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-amber-500/80 mb-1.5">System Access Key</label>
                  <div className="w-full bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 text-center">
                    <span className="text-amber-200 font-mono font-bold text-sm tracking-widest">admin@123</span>
                    <p className="text-[10px] text-amber-500/60 mt-0.5 uppercase tracking-tighter">Fixed Default Password</p>
                  </div>
                </div>
              </div>
              {newUser.role === 'Student' && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="block text-sm font-medium text-fuchsia-400 mb-1.5">Assign Batch (Optional)</label>
                  <select 
                    value={newUser.batchIdStr}
                    onChange={(e) => setNewUser({...newUser, batchIdStr: e.target.value})}
                    className="w-full bg-slate-950 border border-fuchsia-500/30 rounded-xl p-3 text-slate-200 focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500 outline-none transition-all"
                  >
                    <option value="">{batches.length === 0 ? "Assign Batch First" : "No Batch Assignment"}</option>
                    {batches.map(b => (
                      <option key={b.id} value={b.batchIdStr}>{b.name} ({b.batchIdStr})</option>
                    ))}
                  </select>
                </div>
              )}
              <button 
                type="submit" 
                disabled={addingUser}
                className="w-full py-3.5 mt-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-700 text-white font-bold text-lg transition-all flex justify-center items-center gap-2"
              >
                {addingUser ? (
                  <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Provisioning...</>
                ) : 'Create Identity'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showEditUserModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-xl w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-amber-400"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg></span>
                  Modify Profile
                </h2>
                <p className="text-sm text-slate-400 mt-1">Reviewing settings for <span className="font-mono text-amber-500 font-bold">{editingUserForm.idStr}</span></p>
              </div>
              <button onClick={() => setShowEditUserModal(false)} className="text-slate-500 hover:text-white transition-colors p-2 hover:bg-slate-800 rounded-full">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">Full Name</label>
                  <input 
                    type="text" 
                    required
                    value={editingUserForm.name}
                    onChange={(e) => setEditingUserForm({...editingUserForm, name: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">Email / Login ID</label>
                  <input 
                    type="email" 
                    required
                    value={editingUserForm.email}
                    onChange={(e) => setEditingUserForm({...editingUserForm, email: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">Role Constraint</label>
                  <select 
                    value={editingUserForm.role}
                    onChange={(e) => setEditingUserForm({...editingUserForm, role: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all"
                  >
                    <option value="Student">Student</option>
                    <option value="Teacher">Teacher</option>
                    <option value="Institute">Administrator</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-rose-400/80 mb-1.5">Reset Password (Optional)</label>
                  <input 
                    type="text" 
                    placeholder="Leave blank to keep current"
                    value={editingUserForm.password}
                    onChange={(e) => setEditingUserForm({...editingUserForm, password: e.target.value})}
                    className="w-full bg-slate-950 border border-rose-500/20 rounded-xl p-3 text-slate-200 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all placeholder:text-slate-600"
                  />
                </div>
              </div>

              {editingUserForm.role === 'Teacher' && (
                <div className="grid grid-cols-3 gap-4 animate-in fade-in slide-in-from-top-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1.5">Department</label>
                    <input 
                      type="text" 
                      value={editingUserForm.department}
                      onChange={(e) => setEditingUserForm({...editingUserForm, department: e.target.value})}
                      placeholder="e.g. Physics"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1.5">Initials</label>
                    <input 
                      type="text" 
                      value={editingUserForm.initials}
                      onChange={(e) => setEditingUserForm({...editingUserForm, initials: e.target.value})}
                      placeholder="e.g. AB"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1.5">Title</label>
                    <input 
                      type="text" 
                      value={editingUserForm.title}
                      onChange={(e) => setEditingUserForm({...editingUserForm, title: e.target.value})}
                      placeholder="e.g. Dr. / Prof."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 outline-none"
                    />
                  </div>
                </div>
              )}

              {editingUserForm.role === 'Student' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">Phone Number</label>
                      <input 
                        type="text" 
                        value={editingUserForm.phoneNumber}
                        onChange={(e) => setEditingUserForm({...editingUserForm, phoneNumber: e.target.value})}
                        placeholder="e.g. 9876543210"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">Class</label>
                      <input 
                        type="text" 
                        value={editingUserForm.studentClass}
                        onChange={(e) => setEditingUserForm({...editingUserForm, studentClass: e.target.value})}
                        placeholder="e.g. 12th"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 outline-none"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">Stream</label>
                      <input 
                        type="text" 
                        value={editingUserForm.stream}
                        onChange={(e) => setEditingUserForm({...editingUserForm, stream: e.target.value})}
                        placeholder="e.g. Science"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">Board</label>
                      <input 
                        type="text" 
                        value={editingUserForm.board}
                        onChange={(e) => setEditingUserForm({...editingUserForm, board: e.target.value})}
                        placeholder="e.g. CBSE"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-amber-500 outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}
              {editingUserForm.role === 'Student' && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="block text-sm font-medium text-fuchsia-400 mb-1.5">Modify Batch Linking</label>
                  <select 
                    value={editingUserForm.batchIdStr}
                    onChange={(e) => setEditingUserForm({...editingUserForm, batchIdStr: e.target.value})}
                    className="w-full bg-slate-950 border border-fuchsia-500/30 rounded-xl p-3 text-slate-200 focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500 outline-none transition-all"
                  >
                    <option value="">{batches.length === 0 ? "Assign Batch First" : "No Batch Assignment"}</option>
                    {batches.map(b => (
                      <option key={b.id} value={b.batchIdStr}>{b.name} ({b.batchIdStr})</option>
                    ))}
                  </select>
                </div>
              )}
              <button 
                type="submit" 
                disabled={updatingUser}
                className="w-full py-3.5 mt-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-50 border border-slate-700 hover:border-amber-500/50 text-white font-bold text-lg transition-colors flex justify-center items-center gap-2"
              >
                {updatingUser ? 'Writing to Database...' : 'Save System Profile'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Upload Attendance Modal */}
      {showUploadAttendanceModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-blue-400"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg></span>
                  Department Attendance Upload
                </h2>
                <p className="text-sm text-slate-400 mt-1">Submit bulk attendance for all teachers via Excel/CSV.</p>
              </div>
              <button onClick={() => { setShowUploadAttendanceModal(false); setAttendanceFile(null); }} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            <form className="space-y-6" onSubmit={handleUploadAttendance}>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-400">Attendance Type</label>
                <div className="flex gap-4">
                  <button 
                    type="button" 
                    onClick={() => setAttendanceType('Teacher')}
                    className={`flex-1 py-2.5 rounded-xl border transition-all flex items-center justify-center gap-2 font-medium ${attendanceType === 'Teacher' ? 'bg-blue-500/20 border-blue-500 text-blue-400' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'}`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    Faculty
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setAttendanceType('Student')}
                    className={`flex-1 py-2.5 rounded-xl border transition-all flex items-center justify-center gap-2 font-medium ${attendanceType === 'Student' ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'}`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                    Students
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-center bg-slate-950/50 border border-slate-800 p-4 rounded-xl">
                 <div className="text-sm text-slate-400">Need the {attendanceType} template?</div>
                 <button type="button" onClick={() => {
                   if (attendanceType === 'Teacher') downloadSampleAttendance();
                   else {
                     const link = document.createElement('a');
                     link.href = "/Sample_Student_Attendance.csv";
                     link.download = "Sample_Student_Attendance.csv";
                     link.click();
                   }
                 }} className="text-xs font-bold uppercase tracking-wider bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg border border-slate-700">Download</button>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">Register File (.xlsx, .csv)</label>
                <div className={`relative group cursor-pointer h-32 w-full rounded-2xl border-2 border-dashed transition-colors flex flex-col items-center justify-center text-center p-4 ${attendanceType === 'Teacher' ? 'border-blue-500/30 hover:border-blue-500 bg-blue-500/5' : 'border-emerald-500/30 hover:border-emerald-500 bg-emerald-500/5'}`}>
                  <input 
                    type="file" 
                    accept=".xlsx,.xls,.csv" 
                    onChange={(e) => setAttendanceFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                  />
                  {!attendanceFile ? (
                    <>
                      <svg className={`w-8 h-8 mb-2 transition-colors ${attendanceType === 'Teacher' ? 'text-blue-400' : 'text-emerald-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                      <p className="text-sm text-slate-400">Drag & drop your file here or click</p>
                    </>
                  ) : (
                    <>
                      <svg className="w-8 h-8 text-emerald-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      <p className="text-sm text-emerald-300 font-medium truncate max-w-full">{attendanceFile.name}</p>
                    </>
                  )}
                </div>
              </div>

              <button 
                type="submit" 
                disabled={uploading || !attendanceFile}
                className={`w-full py-3.5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-lg transition-all shadow-lg flex justify-center items-center gap-2 ${attendanceType === 'Teacher' ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/20' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20'}`}
              >
                {uploading ? (
                  <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Processing...</>
                ) : `Upload ${attendanceType} Data`}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Master Leave Quota Modal */}
      {showMasterLeaveModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-purple-400"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></span>
                  Annual Leave Setup
                </h2>
                <p className="text-sm text-slate-400 mt-1">Configure leave allocation for faculty members.</p>
              </div>
              <button onClick={() => setShowMasterLeaveModal(false)} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            <form className="space-y-4" onSubmit={async (e) => {
              e.preventDefault();
              if (!selectedTeacherEmailForLeave) {
                toast.error("Please select a teacher first.");
                return;
              }
              try {
                const quotas = [
                  { leaveType: 'Casual', allocatedCount: parseInt(leaveQuotas.cl) },
                  { leaveType: 'Sick', allocatedCount: parseInt(leaveQuotas.sl) },
                  { leaveType: 'Privileged', allocatedCount: parseInt(leaveQuotas.pl) }
                ];
                await axios.post(`${API_BASE_URL}/api/teachers/email/${selectedTeacherEmailForLeave}/leave-quota`, quotas);
                toast("Leave quotas updated successfully!");
                setShowMasterLeaveModal(false);
              } catch (err) {
                console.error(err);
                toast("Failed to update leave quotas.");
              }
            }}>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">Select Teacher</label>
                <select 
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-purple-500 transition-all outline-none"
                  value={selectedTeacherEmailForLeave}
                  onChange={(e) => setSelectedTeacherEmailForLeave(e.target.value)}
                  required
                >
                  <option value="">Select a Teacher...</option>
                  {users.filter(u => u.role === "Teacher").map(u => (
                    <option key={u.email} value={u.email}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">Casual Leave (CL)</label>
                  <input 
                    type="number" 
                    value={leaveQuotas.cl}
                    onChange={(e) => setLeaveQuotas({...leaveQuotas, cl: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-purple-500 transition-all outline-none" 
                    placeholder="e.g. 12"
                    min="0"
                    required 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">Sick Leave (SL)</label>
                  <input 
                    type="number" 
                    value={leaveQuotas.sl}
                    onChange={(e) => setLeaveQuotas({...leaveQuotas, sl: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-purple-500 transition-all outline-none" 
                    placeholder="e.g. 10"
                    min="0"
                    required 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">Privileged Leave (PL)</label>
                  <input 
                    type="number" 
                    value={leaveQuotas.pl}
                    onChange={(e) => setLeaveQuotas({...leaveQuotas, pl: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-purple-500 transition-all outline-none" 
                    placeholder="e.g. 5"
                    min="0"
                    required 
                  />
                </div>
              </div>

              <div className="pt-2">
                <button type="submit" className="w-full py-3 rounded-xl bg-purple-500 hover:bg-purple-600 text-white font-bold text-lg transition-all shadow-lg shadow-purple-500/20">
                  Save Leave Policy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Biometric Setup Modal */}
      {showBiometricModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-2xl w-full shadow-2xl animate-in zoom-in-95 duration-200 relative overflow-hidden flex flex-col max-h-[90vh]">
             <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 blur-[60px] rounded-full pointer-events-none"></div>
            
            <div className="flex justify-between items-center mb-6 relative z-10">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-emerald-400"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>
                  API Attendance Builder
                </h2>
                <p className="text-sm text-slate-400 mt-1">Construct a generic API request to sync attendance data.</p>
              </div>
              <button onClick={() => setShowBiometricModal(false)} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-1 p-1 bg-slate-950 rounded-xl mb-6 relative z-10">
              {['URL', 'Headers', 'Params', 'Mapping'].map(tab => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setBiometricActiveTab(tab)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${biometricActiveTab === tab ? 'bg-slate-800 text-emerald-400 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <form className="flex-1 overflow-y-auto space-y-4 relative z-10 pr-2 custom-scrollbar" onSubmit={handleSaveBiometricSettings}>
              {biometricActiveTab === 'URL' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">Request URL</label>
                      <input 
                        type="text" 
                        value={biometricForm.biometricApiUrl}
                        onChange={(e) => setBiometricForm({...biometricForm, biometricApiUrl: e.target.value})}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-emerald-500 transition-all outline-none" 
                        placeholder="https://api.example.com/attendance"
                        required 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">Method</label>
                      <select 
                        value={biometricForm.biometricApiMethod}
                        onChange={(e) => setBiometricForm({...biometricForm, biometricApiMethod: e.target.value})}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-emerald-500 transition-all outline-none"
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">Corporate ID</label>
                      <input 
                        type="text" 
                        value={biometricForm.biometricCorporateId}
                        onChange={(e) => setBiometricForm({...biometricForm, biometricCorporateId: e.target.value})}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-emerald-500 transition-all outline-none" 
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">Username</label>
                      <input 
                        type="text" 
                        value={biometricForm.biometricUsername}
                        onChange={(e) => setBiometricForm({...biometricForm, biometricUsername: e.target.value})}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-emerald-500 transition-all outline-none" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">Password</label>
                      <input 
                        type="password" 
                        value={biometricForm.biometricPassword}
                        onChange={(e) => setBiometricForm({...biometricForm, biometricPassword: e.target.value})}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-emerald-500 transition-all outline-none" 
                      />
                    </div>
                  </div>
                </div>
              )}

              {biometricActiveTab === 'Headers' && (
                <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Custom HTTP Headers</span>
                    <button type="button" onClick={addHeader} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg> Add Header
                    </button>
                  </div>
                  {biometricHeaders.map((h, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input 
                        placeholder="Key" 
                        value={h.key} 
                        onChange={(e) => updateHeader(i, 'key', e.target.value)}
                        className="flex-1 bg-slate-950 border border-slate-800 rounded-lg p-2 text-sm text-slate-200 outline-none focus:border-emerald-500"
                      />
                      <input 
                        placeholder="Value" 
                        value={h.value} 
                        onChange={(e) => updateHeader(i, 'value', e.target.value)}
                        className="flex-1 bg-slate-950 border border-slate-800 rounded-lg p-2 text-sm text-slate-200 outline-none focus:border-emerald-500"
                      />
                      <button type="button" onClick={() => removeHeader(i)} className="text-slate-600 hover:text-red-400 p-1">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {biometricActiveTab === 'Params' && (
                <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Query Parameters</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => {
                        const newParams = [...biometricParams];
                        newParams.push({ key: 'FromDate', value: '{fromDate}' });
                        setBiometricParams(newParams);
                      }} className="text-[10px] bg-slate-800 text-emerald-400 px-2 py-1 rounded hover:bg-slate-700 transition-colors">
                        + Dynamic FromDate
                      </button>
                      <button type="button" onClick={() => {
                        const newParams = [...biometricParams];
                        newParams.push({ key: 'ToDate', value: '{toDate}' });
                        setBiometricParams(newParams);
                      }} className="text-[10px] bg-slate-800 text-emerald-400 px-2 py-1 rounded hover:bg-slate-700 transition-colors">
                        + Dynamic ToDate
                      </button>
                      <button type="button" onClick={addParam} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 ml-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg> Add Param
                      </button>
                    </div>
                  </div>
                  {biometricParams.map((p, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input 
                        placeholder="Key" 
                        value={p.key} 
                        onChange={(e) => updateParam(i, 'key', e.target.value)}
                        className="flex-1 bg-slate-950 border border-slate-800 rounded-lg p-2 text-sm text-slate-200 outline-none focus:border-emerald-500"
                      />
                      <input 
                        placeholder="Value" 
                        value={p.value} 
                        onChange={(e) => updateParam(i, 'value', e.target.value)}
                        className="flex-1 bg-slate-950 border border-slate-800 rounded-lg p-2 text-sm text-slate-200 outline-none focus:border-emerald-500"
                      />
                      <button type="button" onClick={() => removeParam(i)} className="text-slate-600 hover:text-red-400 p-1">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {biometricActiveTab === 'Mapping' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="p-3 bg-slate-950/50 rounded-xl border border-slate-800 mb-2">
                     <p className="text-[11px] text-slate-400 leading-relaxed italic">
                        Map the JSON response fields from your API to PenSense fields.
                     </p>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Data Root Field (if JSON is an object)</label>
                      <input 
                        type="text" 
                        value={biometricMapping.dataRoot || ''}
                        onChange={(e) => setBiometricMapping({...biometricMapping, dataRoot: e.target.value})}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-slate-200 outline-none focus:border-emerald-500"
                        placeholder="e.g. data or records (Leave blank if root is a List)"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Employee/Student ID Field</label>
                      <input 
                        type="text" 
                        value={biometricMapping.empCodeField}
                        onChange={(e) => setBiometricMapping({...biometricMapping, empCodeField: e.target.value})}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-slate-200 outline-none focus:border-emerald-500"
                        placeholder="e.g. EmpCode or userId"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                       <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Date Field</label>
                          <input 
                            type="text" 
                            value={biometricMapping.dateField}
                            onChange={(e) => setBiometricMapping({...biometricMapping, dateField: e.target.value})}
                            className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-slate-200 outline-none focus:border-emerald-500"
                            placeholder="e.g. PunchDate"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Time Field</label>
                          <input 
                            type="text" 
                            value={biometricMapping.timeField}
                            onChange={(e) => setBiometricMapping({...biometricMapping, timeField: e.target.value})}
                            className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-slate-200 outline-none focus:border-emerald-500"
                            placeholder="e.g. PunchTime"
                          />
                        </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-slate-800">
                <div className="flex items-center gap-3 p-4 bg-slate-950 rounded-2xl border border-slate-800 mb-4">
                  <input 
                    type="checkbox" 
                    id="enableBiometric"
                    checked={biometricForm.biometricApiEnabled}
                    onChange={(e) => setBiometricForm({...biometricForm, biometricApiEnabled: e.target.checked})}
                    className="w-5 h-5 rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
                  />
                  <label htmlFor="enableBiometric" className="text-sm font-medium text-slate-200 cursor-pointer">
                    Enable Periodic Background Sync (Every 5 Minutes)
                  </label>
                </div>

                <div className="p-4 bg-slate-950/50 rounded-2xl border border-slate-800 mb-6 space-y-2">
                  <div className="flex items-center gap-2 mb-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-400"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Automation Scheduler</span>
                  </div>
                  <p className="text-[11px] text-slate-200">
                    Background sync is active every 5 minutes.
                  </p>
                  <p className="text-[10px] text-slate-500 italic">
                    The system will automatically fetch new records from the biometric API periodically.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button 
                    type="button" 
                    onClick={handleTriggerManualSync}
                    disabled={syncing}
                    className="py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold transition-all border border-slate-700 disabled:opacity-50"
                  >
                    {syncing ? 'Syncing...' : 'Test & Sync Now'}
                  </button>
                  <button 
                    type="submit" 
                    disabled={syncing}
                    className="py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                  >
                    Save API Configuration
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
